import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import AssetService from '../../../Services/assets.service';
import { useDirectionByLanguage } from '../../../hooks/useDirectionByLanguage';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';
import { xtramInputProps, xtramTextAlign } from './AssetModalShell/assetModalInputs';

const AddAssetGroup = ({ open, onClose, caseId, refreshAssetGroups, assetGroups, token }) => {
    const { i18n, t } = useTranslation();
    const direction = useDirectionByLanguage(i18n);
    const [newAssetGroup, setNewAssetGroup] = useState({
        CaseId: caseId || "",
        assetGroupId: "",
        name: "",
        Description: "",
        depreciationPercentage: "",
    });

    const [isSaveDisabled, setIsSaveDisabled] = useState(true); // State to manage save button disabled state


    // Calculate the default assetGroupId when the component mounts or when assetGroups change
    useEffect(() => {

        if (assetGroups && assetGroups.length > 0) {
            // Find the maximum assetGroupId in the assetGroups array
            const maxAssetGroupId = Math.max(...assetGroups.map(group => group.assetGroupId || 0));
            // Set the default assetGroupId to maxAssetGroupId + 1
            setNewAssetGroup(prev => ({ ...prev, assetGroupId: maxAssetGroupId + 1 }));
        } else {
            // If there are no asset groups, start with 1
            setNewAssetGroup(prev => ({ ...prev, assetGroupId: 1 }));
        }
    }, [assetGroups]);

    // Validation function to check if required fields are filled
    const validateFields = () => {

        const { assetGroupId, name, depreciationPercentage } = newAssetGroup;
        return assetGroupId !== '' && name.trim() !== '' && depreciationPercentage.trim() !== '';
    };

    // Update validation state whenever newAssetGroup changes
    useEffect(() => {
        setIsSaveDisabled(!validateFields());
    }, [newAssetGroup]);

    const inputProps = xtramInputProps(direction);
    const inputStyle = xtramTextAlign(direction);

    const handleChange = (field, value) => {
        setNewAssetGroup((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!validateFields()) {
            toast.error(t('Please fill all required fields'));
            return;
        }

        const payload = {
            caseId: caseId?.toString(),
            assetGroupId: newAssetGroup.assetGroupId?.toString() || "",
            name: newAssetGroup.name,
            description: newAssetGroup.description || "",
            depreciationPercentage: parseFloat(newAssetGroup.depreciationPercentage),
        };

        
        

        try {
            const response = await AssetService.CreateAssetGroup(payload, token);
            if (response.success) {
                toast.success(t('AssetGroupAddedSuccessfully'));
                refreshAssetGroups();
                // onClose();
            } else {
                toast.error(t('FailedToAddAssetGroup'));
            }
        } catch (error) {
            console.error(error);
            toast.error(t('AnErrorOccurredWhileAddingAssetGroup'));
        }
    };


    if (!open) return null;

    const fields = [
        { key: 'assetGroupId', label: t('AssetGroupId'), value: newAssetGroup.assetGroupId, required: true, type: 'number', field: 'assetGroupId' },
        { key: 'name', label: t('AssetGroupName'), value: newAssetGroup.name, required: true, field: 'name' },
        { key: 'depreciationPercentage', label: t('DepreciationPercentage'), value: newAssetGroup.depreciationPercentage, required: true, type: 'number', field: 'depreciationPercentage' },
        { key: 'description', label: t('Description'), value: newAssetGroup.description || '', field: 'description', full: true },
    ];

    return (
        <AssetModalShell
            open={open}
            onClose={onClose}
            title={t('AddAssetGroup')}
            direction={direction}
            size="sm"
            footer={(
                <>
                    <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                        {t('Cancel')}
                    </button>
                    <button type="button" className={shell.primaryBtn} onClick={handleSave} disabled={isSaveDisabled}>
                        <SaveIcon />
                        {t('Save')}
                    </button>
                </>
            )}
        >
            <div className={shell.fieldGrid}>
                {fields.map((f) => (
                    <div key={f.key} className={`${shell.field}${f.full ? ` ${shell.fieldFull}` : ''}`}>
                        <span className={shell.fieldLabel}>
                            <span>{f.required && <span className={shell.requiredMark}>*</span>}{f.label}</span>
                        </span>
                        <TextField
                            fullWidth
                            placeholder={f.label}
                            value={f.value}
                            onChange={(e) => handleChange(f.field, e.target.value)}
                            type={f.type || 'text'}
                            variant="standard"
                            InputProps={inputProps}
                            inputProps={inputStyle}
                        />
                    </div>
                ))}
            </div>
        </AssetModalShell>
    );
};

export default AddAssetGroup;